import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import type { AvailabilityMap, AvailabilityStatus, Contributor, ContributorWeekSchedule, DateAvailabilityMap, Solution, WeekSchedulePayload } from '../types';
import { StatusToggle } from './StatusToggle';
import { resolveEffectiveStatus } from '../utils/availabilityResolver';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
function getDatesForWeek(weekStart: string): string[] {
  const dates: string[] = [];
  const start = new Date(weekStart + 'T00:00:00Z');
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
}
function cycleStatus(current: AvailabilityStatus): AvailabilityStatus {
  const cycle: AvailabilityStatus[] = ['available', 'limited', 'unavailable'];
  return cycle[(cycle.indexOf(current) + 1) % cycle.length];
}

// Mirrors the OOO check in AvailabilityGrid but for any date, not just today.
function isDateOOO(date: string, contributor: Contributor): boolean {
  if (contributor.oooStartDate && contributor.oooEndDate) {
    return contributor.oooStartDate <= date && contributor.oooEndDate >= date;
  }
  return !!contributor.isOutOfOffice;
}
interface Props {
  contributor: Contributor;
  solutions: Solution[];
  baseAvailability: AvailabilityMap;
  dateAvailability: DateAvailabilityMap;
  canEdit: boolean;
  onClose: () => void;
  fetchCurrentWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  fetchNextWeek: (contributorId: string) => Promise<WeekSchedulePayload | null>;
  onUpdateDailyAvailability: (solutionId: string, date: string, status: AvailabilityStatus | null) => Promise<void>;
  /** true (default) = two-tab slide-over panel; false = single 14-column inline grid */
  showTabs?: boolean;
}
export function ContributorWeekView({
  contributor,
  solutions,
  baseAvailability,
  dateAvailability,
  canEdit,
  onClose,
  fetchCurrentWeek,
  fetchNextWeek,
  onUpdateDailyAvailability,
  showTabs = true
}: Props) {
  const [currentWeek, setCurrentWeek] = useState<WeekSchedulePayload | null>(null);
  const [nextWeek, setNextWeek] = useState<WeekSchedulePayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [markingSolutions, setMarkingSolutions] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'current' | 'next'>('current');
  const today = new Date().toISOString().split('T')[0];
  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCurrentWeek(contributor.id), fetchNextWeek(contributor.id)]).then(([cw, nw]) => {
      setCurrentWeek(cw);
      setNextWeek(nw);
      setLoading(false);
    });
  }, [contributor.id]);
  const activeWeek = activeTab === 'current' ? currentWeek : nextWeek;
  const setActiveWeek = activeTab === 'current' ? setCurrentWeek : setNextWeek;
  const handleCellClick = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, date: string) => {
    if (!canEdit) return;
    const key = `${date}_${solutionId}`;
    setUpdating(key);
    const currentStatus = resolveEffectiveStatus(date, contributor.id, solutionId, {
      [contributor.id]: weekPayload.schedule[date] ?? {}
    }, dateAvailability, baseAvailability) ?? 'unavailable';
    const newStatus = cycleStatus(currentStatus);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule,
        [date]: {
          ...(prev.schedule[date] ?? {}),
          [solutionId]: newStatus
        }
      };
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    await onUpdateDailyAvailability(solutionId, date, newStatus);
    setUpdating(null);
  };

  // Bulk-sets all 7 days of a single solution's row to `status` for the given week.
  const handleMarkSolutionWeek = async (weekPayload: WeekSchedulePayload, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>, solutionId: string, status: AvailabilityStatus) => {
    if (!canEdit) return;
    const markKey = `${weekPayload.weekKey}_${solutionId}`;
    if (markingSolutions[markKey]) return;
    setMarkingSolutions(prev => ({
      ...prev,
      [markKey]: true
    }));
    const dates = getDatesForWeek(weekPayload.weekStart);
    setWeekFn(prev => {
      if (!prev) return prev;
      const updatedSchedule: ContributorWeekSchedule = {
        ...prev.schedule
      };
      for (const date of dates) {
        updatedSchedule[date] = {
          ...(updatedSchedule[date] ?? {}),
          [solutionId]: status
        };
      }
      return {
        ...prev,
        schedule: updatedSchedule
      };
    });
    try {
      await Promise.all(dates.map(date => onUpdateDailyAvailability(solutionId, date, status)));
    } finally {
      setMarkingSolutions(prev => {
        const next = {
          ...prev
        };
        delete next[markKey];
        return next;
      });
    }
  };

  // Used by the tabbed view (slide-over panel)
  const renderWeekGrid = (weekPayload: WeekSchedulePayload | null, setWeekFn: React.Dispatch<React.SetStateAction<WeekSchedulePayload | null>>) => {
    if (!weekPayload) {
      return <div className="p-4 text-slate-500 text-sm" data-id="element-55">Failed to load week schedule.</div>;
    }
    const dates = getDatesForWeek(weekPayload.weekStart);
    return <table className="w-full text-xs border-collapse" data-id="element-56">
        <thead data-id="element-57">
          <tr data-id="element-58">
            <th className="text-left py-2 px-3 text-slate-500 font-medium w-48 min-w-[160px]" data-id="element-59">
              Solution
            </th>
            {dates.map((date, i) => {
            const isToday = date === today;
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            return <th key={date} className={`text-center py-2 px-2 font-medium w-16 ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-60">
                  <div data-id="element-61">{DAY_LABELS[i]}</div>
                  <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-62">
                    {date.slice(5)}
                  </div>
                  {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-63" />}
                  {isOOO && <div className="text-[9px] mt-0.5 font-medium" data-id="element-64">OOO</div>}
                </th>;
          })}
          </tr>
        </thead>
        <tbody data-id="element-65">
          {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-66">
              <td className="py-2 px-3 font-medium max-w-[160px]" data-id="element-67">
                <div className="flex flex-col gap-0.5" data-id="element-68">
                  <span className="text-slate-700 truncate" data-id="element-69">{solution.name}</span>
                  {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1" data-id="element-70">
                      {markingSolutions[`${weekPayload.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-71" /> : (['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(weekPayload, setWeekFn, solution.id, s)} title={`Mark whole week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-72" />)}
                    </div>}
                </div>
              </td>
              {dates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = weekPayload.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(date, contributor.id, solution.id, weeklyScheduleForDate, dateAvailability, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            const hasWeeklyOverride = weeklyOverride !== undefined;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-50/70' : ''}`} data-id="element-73">
                    <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : ''}`} data-id="element-74">
                      {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-75" /> : <StatusToggle status={status} onClick={() => handleCellClick(weekPayload, setWeekFn, solution.id, date)} disabled={!canEdit || isOOO} data-id="element-76" />}
                      {hasWeeklyOverride && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-77" />}
                    </div>
                  </td>;
          })}
            </tr>)}
        </tbody>
      </table>;
  };

  // Used by the inline grid view (name click) — all 14 dates in one horizontally-scrollable table
  const renderCombinedGrid = (cw: WeekSchedulePayload | null, nw: WeekSchedulePayload | null) => {
    const currentDates = cw ? getDatesForWeek(cw.weekStart) : [];
    const nextDates = nw ? getDatesForWeek(nw.weekStart) : [];
    const renderDateTh = (date: string, i: number, isFirstOfNextWeek: boolean) => {
      const isToday = date === today;
      const isWeekend = i >= 5;
      const isOOO = isDateOOO(date, contributor);
      return <th key={date} className={`text-center py-2 px-1 font-medium ${isFirstOfNextWeek ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100 text-slate-400' : isToday ? 'text-[#EB1000]' : isWeekend ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-78">
          <div data-id="element-79">{DAY_LABELS[i]}</div>
          <div className={`text-[10px] font-normal mt-0.5 ${isToday && !isOOO ? 'font-semibold' : ''}`} data-id="element-80">
            {date.slice(5)}
          </div>
          {isToday && !isOOO && <div className="mx-auto mt-1 h-1 w-1 rounded-full bg-[#EB1000]" data-id="element-81" />}
          {isOOO && <div className="text-[9px] mt-0.5 font-medium" data-id="element-82">OOO</div>}
        </th>;
    };
    return <table className="w-full table-fixed text-xs border-collapse" data-id="element-83">
          <thead data-id="element-84">
            {/* Week group labels */}
            <tr data-id="element-85">
              <th className="py-1.5 px-3" style={{
            width: '160px'
          }} data-id="element-86" />
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100" data-id="element-87">
                This Week
                {cw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-88">
                    {cw.weekStart.slice(5)} – {cw.weekEnd.slice(5)}
                  </span>}
              </th>
              <th colSpan={7} className="text-center py-1.5 text-[11px] font-semibold text-slate-600 border-b border-slate-100 border-l-2 border-l-slate-300" data-id="element-89">
                Next Week
                {nw && <span className="ml-1.5 font-normal text-slate-400" data-id="element-90">
                    {nw.weekStart.slice(5)} – {nw.weekEnd.slice(5)}
                  </span>}
              </th>
            </tr>
            {/* Day headers */}
            <tr data-id="element-91">
              <th className="text-left py-2 px-3 text-slate-500 font-medium" style={{
            width: '160px'
          }} data-id="element-92">
                Solution
              </th>
              {currentDates.map((date, i) => renderDateTh(date, i, false))}
              {nextDates.map((date, i) => renderDateTh(date, i, i === 0))}
            </tr>
          </thead>
          <tbody data-id="element-93">
            {solutions.map(solution => <tr key={solution.id} className="group border-t border-slate-100 hover:bg-slate-50/50 transition-colors" data-id="element-94">
                <td className="py-2 px-3 font-medium" style={{
            width: '160px'
          }} data-id="element-95">
                  <div className="flex flex-col gap-0.5" data-id="element-96">
                    <span className="text-slate-700 truncate" data-id="element-97">{solution.name}</span>
                    {canEdit && <div className="opacity-0 group-hover:opacity-100 transition-opacity flex flex-col gap-0.5" data-id="element-98">
                        {cw && <div className="flex items-center gap-0.5" data-id="element-99">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-100">CW:</span>
                            {markingSolutions[`${cw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-101" /> : (['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(cw, setCurrentWeek, solution.id, s)} title={`Mark current week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-102" />)}
                          </div>}
                        {nw && <div className="flex items-center gap-0.5" data-id="element-103">
                            <span className="text-[9px] text-slate-400 shrink-0 mr-0.5" data-id="element-104">NW:</span>
                            {markingSolutions[`${nw.weekKey}_${solution.id}`] ? <Loader2 className="h-3 w-3 animate-spin text-slate-400" data-id="element-105" /> : (['available', 'limited', 'unavailable'] as AvailabilityStatus[]).map(s => <button key={s} onClick={() => handleMarkSolutionWeek(nw, setNextWeek, solution.id, s)} title={`Mark next week: ${s}`} className={`h-3.5 w-3.5 rounded-full flex-shrink-0 transition-transform hover:scale-110 ${s === 'available' ? 'bg-emerald-400 hover:bg-emerald-500' : s === 'limited' ? 'bg-amber-400 hover:bg-amber-500' : 'bg-red-500 hover:bg-red-600'}`} data-id="element-106" />)}
                          </div>}
                      </div>}
                  </div>
                </td>
                {/* Current week cells */}
                {currentDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = cw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(date, contributor.id, solution.id, weeklyScheduleForDate, dateAvailability, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-50/70' : ''}`} data-id="element-107">
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : ''}`} data-id="element-108">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-109" /> : <StatusToggle status={status} onClick={() => cw && handleCellClick(cw, setCurrentWeek, solution.id, date)} disabled={!canEdit || !cw || isOOO} data-id="element-110" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-111" />}
                      </div>
                    </td>;
          })}
                {/* Next week cells */}
                {nextDates.map((date, i) => {
            const isWeekend = i >= 5;
            const isFirstCell = i === 0;
            const isOOO = isDateOOO(date, contributor);
            const weeklyOverride = nw?.schedule[date]?.[solution.id];
            const weeklyScheduleForDate = weeklyOverride !== undefined ? {
              [contributor.id]: {
                [solution.id]: weeklyOverride
              }
            } : {};
            const status = resolveEffectiveStatus(date, contributor.id, solution.id, weeklyScheduleForDate, dateAvailability, baseAvailability) ?? 'unavailable';
            const key = `${date}_${solution.id}`;
            const isUpdating = updating === key;
            return <td key={date} className={`py-2 px-2 text-center ${isFirstCell ? 'border-l-2 border-slate-300' : ''} ${isOOO ? 'bg-slate-100' : isWeekend ? 'bg-slate-50/70' : ''}`} data-id="element-112">
                      <div className={`flex items-center justify-center gap-1 ${isOOO ? 'opacity-40' : ''}`} data-id="element-113">
                        {isUpdating ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" data-id="element-114" /> : <StatusToggle status={status} onClick={() => nw && handleCellClick(nw, setNextWeek, solution.id, date)} disabled={!canEdit || !nw || isOOO} data-id="element-115" />}
                        {weeklyOverride !== undefined && !isUpdating && !isOOO && <span className="h-1.5 w-1.5 rounded-full bg-blue-400 flex-shrink-0" title="Weekly override set" data-id="element-116" />}
                      </div>
                    </td>;
          })}
              </tr>)}
          </tbody>
        </table>;
  };
  const legend = <p className="mt-3 text-xs text-slate-400" data-id="element-117">
      <span className="inline-flex items-center gap-1" data-id="element-118">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-400 inline-block" data-id="element-119" />
        Weekly override set &nbsp;·&nbsp; Click a circle to cycle: available → limited → unavailable
      </span>
    </p>;
  if (loading) {
    return <div className="flex items-center justify-center p-12" data-id="element-120">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" data-id="element-121" />
        <span className="ml-2 text-slate-500 text-sm" data-id="element-122">Loading week schedule…</span>
      </div>;
  }
  return <div className="flex flex-col h-full" data-id="element-123">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white" data-id="element-124">
        <div data-id="element-125">
          <h2 className="text-base font-semibold text-slate-900" data-id="element-126">{contributor.name}</h2>
          <p className="text-xs text-slate-500 mt-0.5" data-id="element-127">Weekly availability — {contributor.region}</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors" aria-label="Close week view" data-id="element-128">
          <X className="h-4 w-4" data-id="element-129" />
        </button>
      </div>

      {showTabs ? <>
          {/* Week tabs */}
          <div className="flex border-b border-slate-200 bg-white px-6" data-id="element-130">
            {(['current', 'next'] as const).map(tab => {
          const week = tab === 'current' ? currentWeek : nextWeek;
          const label = tab === 'current' ? 'This Week' : 'Next Week';
          const range = week ? `${week.weekStart.slice(5)} – ${week.weekEnd.slice(5)}` : '';
          return <button key={tab} onClick={() => setActiveTab(tab)} className={`mr-6 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab ? 'border-[#EB1000] text-[#EB1000]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-131">
                  {label}
                  {range && <span className="ml-1.5 text-xs font-normal opacity-70" data-id="element-132">{range}</span>}
                </button>;
        })}
          </div>
          <div className="overflow-auto flex-1 p-4" data-id="element-133">
            {activeWeek ? renderWeekGrid(activeWeek, setActiveWeek) : <div className="p-4 text-slate-500 text-sm" data-id="element-134">Failed to load week schedule.</div>}
            {legend}
          </div>
        </> : (/* Single 14-column pane — horizontally scrollable */
    <div className="overflow-auto flex-1 p-4" data-id="element-135">
          {renderCombinedGrid(currentWeek, nextWeek)}
          {legend}
        </div>)}
    </div>;
}